import { Accordion } from '@liveauctioneers/hammer-ui-core/accordion';
import { Checkbox } from '@liveauctioneers/hammer-ui-core/checkbox';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { ViewMore } from '@liveauctioneers/hammer-ui-core/viewMore';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useNavigateSearchPageWithParams } from '@/pages/Search/SearchPage/hooks/useNavigateSearchPageWithParams';
import { CurrentOpenMenu, MAX_FILTER_OPTIONS_COUNT } from '@/pages/Search/SearchPage/types';
import { getRoundedNumberText } from '@/pages/Search/SearchPage/utils/getRoundedNumberText';
import { FacetOption } from '@/types/FacetOption';
import { FilterAction } from '@/types/SearchFacet';
import { getBrowseDimensionKeyFromFacetType } from '@/utils/browsePath/browsePath';
import { modifyParamsRemovePage, modifyParamsWhenChecked, parseQueryParams } from '@/utils/queryParams';
import { isFacetOptionCheckedForSemanticUrl } from '@/utils/search/searchQuery';
import { urlSafeTitle } from '@/utils/urls';

/** Same shape as entries from `getSearchFacets` / `getMergedOptionFacet` (id + options). */
type MergedOptionFacetData = {
    options?: FacetOption[];
};

type Props = {
    facetId: string;
    mergedOptionFacet: MergedOptionFacetData;
    title: string;
};

/** Checkbox list for one dynamic attribute facet (`dynamic_attr_*`). Options come from the parent via `mergedOptionFacet`. */
export const AttributeFacetFilter = ({ facetId, mergedOptionFacet, title }: Props) => {
    const { formatMessage } = useIntl();
    const [isExpanded, setIsExpanded] = useState(false);
    const { search } = useLocation();
    const queryParams = parseQueryParams(search);
    const navigateWithParams = useNavigateSearchPageWithParams();
    const { options = [] } = mergedOptionFacet;
    const isOptionChecked = useCallback(
        (option: FacetOption) =>
            isFacetOptionCheckedForSemanticUrl(
                queryParams as Record<string, unknown>,
                facetId,
                option,
                option.selected
            ),
        [queryParams, facetId]
    );

    const onOptionClick = (option: FacetOption) => {
        const newCheckStatus = !isOptionChecked(option);
        const isSemanticBrowseFacet = Boolean(getBrowseDimensionKeyFromFacetType(facetId));
        const valueForUrl = isSemanticBrowseFacet ? urlSafeTitle(option.name ?? String(option.id)) : option.id;

        let newParams = modifyParamsWhenChecked(queryParams, facetId, valueForUrl, newCheckStatus, {
            useSemanticAttributeParams: true,
        });
        newParams = modifyParamsRemovePage(newParams);

        navigateWithParams({
            currentOpenMenu: CurrentOpenMenu.AllFilters,
            filterAction: newCheckStatus ? FilterAction.AttrFacetChanged : FilterAction.AttrFacetRemoved,
            params: newParams,
            preventScrollReset: true,
        });
    };

    const filteredOptions = useMemo(() => {
        const sortedOptions = options
            .filter((option) => option.count !== 0 || isOptionChecked(option))
            .sort((firstOption, secondOption) => secondOption.count - firstOption.count);
        if (!isExpanded) {
            return sortedOptions.slice(0, MAX_FILTER_OPTIONS_COUNT);
        }
        return sortedOptions;
    }, [isExpanded, options, isOptionChecked]);

    if (filteredOptions.length === 0) {
        return null;
    }

    return (
        <Accordion
            className="w-full"
            density="compact"
            emphasis="subtle"
            headline={title}
            open
        >
            <ViewMore
                collapseText={formatMessage({ id: 'view_less' })}
                expandText={formatMessage({ id: 'view_more' })}
                fadeBackgroundColor="white"
                onChange={(isExpanded) => setIsExpanded(isExpanded)}
                textCutoffHeight={200}
            >
                <div
                    className={cn('flex max-h-[400px] flex-col gap-xs overflow-y-hidden', {
                        'overflow-y-auto': isExpanded,
                    })}
                >
                    {filteredOptions.map((option) => (
                        <Checkbox.Option
                            checked={isOptionChecked(option)}
                            disabled={option.count === 0 && !isOptionChecked(option)}
                            key={option.id}
                            onChange={() => {
                                onOptionClick(option);
                            }}
                        >
                            {option.name} ({getRoundedNumberText({ totalFound: option.count })})
                        </Checkbox.Option>
                    ))}
                </div>
            </ViewMore>
        </Accordion>
    );
};
